import { useState } from 'react';
import {
  AccountLevel,
  Button,
  Panel,
  PlanetVisual,
  ResourceCounter,
  RingBadge,
  Stat,
  StatGrid,
  ViewHeader,
  formatAmount,
  formatCoordinates,
  formatRate,
} from '@spacegame/ui';
import {
  SHIP_KEYS,
  DEFENSE_KEYS,
  UNITS,
  COLONY_MIN_LEVEL,
  MAX_PLANETS,
  productionReport,
  storageReport,
  xpForNextLevel,
} from '@spacegame/engine';
import { useGame, useGameActions } from '../game/GameProvider';
import { QueuePanel } from '../components/SidePanels';
import { planetImage } from '../game/planetArt';

/**
 * Always-visible colonization teaser (galaxy plan Milestone E) — the actual
 * per-planet building management lives in `BuildingsView` (Teil 2); this
 * panel just founds new colonies and, once any exist, lists them with a
 * shortcut into that view with the right planet already selected.
 */
function ColonizationPanel({ onManagePlanet }: { onManagePlanet: (planetIndex: number) => void }) {
  const { game } = useGame();
  const actions = useGameActions();
  const [pending, setPending] = useState(false);

  const planetCount = game.planets.length;
  const atCap = planetCount >= MAX_PLANETS;
  const belowLevel = game.level < COLONY_MIN_LEVEL;
  const hasColonyShip = game.planets[0]!.ships.colonyShip > 0;
  const eligible = !atCap && !belowLevel && hasColonyShip;

  const hint = atCap
    ? 'Maximale Anzahl an Kolonien erreicht.'
    : belowLevel
      ? `Ab Level ${COLONY_MIN_LEVEL} verfügbar (Ring 2).`
      : !hasColonyShip
        ? 'Benötigt ein Kolonieschiff aus der Werft.'
        : 'Ein fremdes System wartet auf Besiedlung.';

  const handleColonize = async () => {
    setPending(true);
    try {
      await actions.colonize();
    } finally {
      setPending(false);
    }
  };

  const openPlanet = (index: number) => {
    actions.setActivePlanetIndex(index);
    onManagePlanet(index);
  };

  return (
    <Panel title="Kolonisierung" eyebrow={`Planeten: ${planetCount}/${MAX_PLANETS}`}>
      {game.planets.length > 1 ? (
        <div className="mb-3 space-y-1.5">
          {game.planets.map((planet, i) => (
            <div key={i} className="flex items-center justify-between gap-2 rounded-control border border-edge-subtle bg-surface-2 px-2.5 py-1.5 text-xs">
              <span>
                {planet.name}
                {i === 0 ? <span className="text-content-muted"> · Heimatwelt</span> : null}
              </span>
              <Button variant="ghost" size="sm" onClick={() => openPlanet(i)}>
                Gebäude verwalten
              </Button>{i > 0 ? <Button variant="danger" size="sm" onClick={() => { if (window.confirm(`Kolonie „${planet.name}“ wirklich aufgeben? Baukosten gehen unwiderruflich verloren.`)) void actions.abandonColony(i); }}>Aufgeben</Button> : null}
            </div>
          ))}
        </div>
      ) : null}
      <p className="text-label text-content-muted">{hint}</p>
      <Button variant="primary" fullWidth disabled={!eligible} loading={pending} onClick={() => void handleColonize()} className="mt-3">
        Kolonie gründen
      </Button>
    </Panel>
  );
}

export function OverviewView({
  onOpenTalents,
  onManagePlanet,
}: {
  onOpenTalents: () => void;
  onManagePlanet: (planetIndex: number) => void;
}) {
  const { game, clock } = useGame();
  const now = clock();
  const report = productionReport(game, now);
  const caps = storageReport(game);
  const surplus = report.energy.produced - report.energy.consumed;

  const fleetValue = SHIP_KEYS.reduce(
    (sum, key) => sum + game.planets[0]!.ships[key] * ((UNITS[key].cost.titan ?? 0) + (UNITS[key].cost.silicate ?? 0)),
    0,
  );
  const fleetCount = SHIP_KEYS.reduce((sum, key) => sum + game.planets[0]!.ships[key], 0);
  const defenseCount = DEFENSE_KEYS.reduce((sum, key) => sum + game.planets[0]!.defense[key], 0);
  const biome = game.planets[0]!.temperature <= -20 ? 'ice' : game.planets[0]!.temperature >= 30 ? 'volcanic' : 'temperate';

  return (
    <>
      <ViewHeader
        eyebrow={formatCoordinates(game.planets[0]!.ring, game.planets[0]!.system, game.planets[0]!.position)}
        title="Command Center"
        actions={<RingBadge ring={game.planets[0]!.ring} showName />}
      />

      <PlanetVisual
        name={game.planets[0]!.name}
        coordinates={formatCoordinates(game.planets[0]!.ring, game.planets[0]!.system, game.planets[0]!.position)}
        biome={biome}
        image={planetImage(biome, game.planets[0]!.name)}
        temperature={game.planets[0]!.temperature}
        moon
        threat={game.event?.type === 'comet'}
        fleets={game.fleetMissions.filter((mission) => mission.kind !== 'campAttack').map((mission) => ({ id: String(mission.id), angle: 120 }))}
      />

      <StatGrid columns={4}>
        <Stat
          label="Energie-Bilanz"
          value={`${surplus >= 0 ? '+' : ''}${formatAmount(surplus)}`}
          accent={surplus >= 0 ? 'power' : 'danger'}
          hint={
            report.energy.factor < 1
              ? `Minen bei ${Math.round(report.energy.factor * 100)} %`
              : 'Minen bei 100 %'
          }
        />
        <Stat label="Flottenwert" value={formatAmount(fleetValue)} accent="silicate" hint={`${fleetCount} Einheiten`} />
        <Stat label="Abwehranlagen" value={String(defenseCount)} accent="danger" hint={game.planets[0]!.defense.interceptor > 0 ? `${game.planets[0]!.defense.interceptor} Abfangraketen` : 'Keine Abfangraketen'} />
        <Stat label="Account-Level" value={String(game.level)} accent="credit" hint={`${formatAmount(game.xp)} / ${formatAmount(xpForNextLevel(game.level))} XP`} />
      </StatGrid>

      {/*
       * On phones the aside (queue/events) stacks below this whole view, which
       * buries the one thing worth checking without scrolling — what's
       * currently being built. Duplicated here, `lg:hidden` — the aside's own
       * copy hides itself for the overview specifically (see App.tsx) so the
       * queue exists exactly once in the accessibility tree at any width.
       */}
      <div className="lg:hidden">
        <QueuePanel />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <Panel title="Rohstoff-Lager" eyebrow="Förderung & Kapazität">
          <div className="grid grid-cols-2 gap-2">
            <ResourceCounter variant="card" resource="titan" amount={game.resources.titan} ratePerHour={report.perHour.titan} capacity={caps.titan} />
            <ResourceCounter variant="card" resource="silicate" amount={game.resources.silicate} ratePerHour={report.perHour.silicate} capacity={caps.silicate} />
            <ResourceCounter variant="card" resource="isotope" amount={game.resources.isotope} ratePerHour={report.perHour.isotope} capacity={caps.isotope} />
            <div className="rounded-panel border border-edge-subtle bg-surface-2 p-3">
              <span className="font-display text-label-sm uppercase text-content-muted">Krater-Bonus</span>
              <div className="mt-1 text-readout font-semibold text-content" data-sg-numeric>
                {game.planets[0]!.crater ? formatRate(report.perHour.silicate / 2) : '—'}
              </div>
              <div className="mt-0.5 text-label text-content-muted">
                {game.planets[0]!.crater ? 'Silikat verdoppelt (24 h)' : 'Kein aktiver Einschlagkrater'}
              </div>
            </div>
          </div>
        </Panel>

        <Panel title="Kommandant" eyebrow={game.playerName}>
          <AccountLevel
            variant="full"
            level={game.level}
            xp={Math.floor(game.xp)}
            xpForNext={xpForNextLevel(game.level)}
            talentPoints={game.talentPoints}
            onOpenTalents={onOpenTalents}
          />
        </Panel>

        <ColonizationPanel onManagePlanet={onManagePlanet} />
      </div>
    </>
  );
}
